import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { registrarSublinea, eliminarSublinea, actualizarSublinea, obtenerSublineas, obtenerLineas } from '../../Services/serviciohttp';
import { API_URLS } from '../../Services/servicioURL';
import { Toast } from 'primereact/toast';

interface LineaType {
    cve: string;
    nombre: string;
}

interface SublineaType {
    cve: string;
    cve_linea: string;
    nombre: string;
}

const Sublineas: React.FC = () => {
    const [mensaje, setMensaje] = useState<string>('');

    const [arrLineas, setLineas] = useState<LineaType[]>([]); 

    const [cveLin, setlineaa] = useState(""); 
    const [nomLin, setLinea] = useState<LineaType | null>(null); 

    const [cveSubLin, setClave] = useState('');
    const [nomSubLin, setNombre] = useState('');
    
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [data, setData] = useState<SublineaType[]>([]);
    
    // Nuevo estado para búsqueda
    const [searchTerm, setSearchTerm] = useState('');
    
    const toast = useRef<Toast>(null);

    // Estado para controlar el modo de operación (view, new, edit)
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    
    // Filtrar datos basado en el término de búsqueda
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        // Convertir el término de búsqueda a minúsculas para búsqueda insensible a mayúsculas
        const searchTermLower = searchTerm.toLowerCase();

        // Filtrar los datos que contengan el término de búsqueda en cualquier campo
        return data.filter(item => 
            String(item.cve).toLowerCase().includes(searchTermLower) || 
            String(item.nombre).toLowerCase().includes(searchTermLower) || 
            String(item.cve_linea).toLowerCase().includes(searchTermLower)
        );
    }, [data, searchTerm]);

    const obtenerLineasDisponibles = async () => {
        try {
            const result = await obtenerLineas(API_URLS.lineas.obtener);
            setLineas(result);
        } catch (error) {
            console.error('Error al obtener las líneas:', error);
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Error al cargar las líneas', 
                life: 3000 
            });
        }
    };

    useEffect(() => {
        obtenerLineasDisponibles();
    }, []);

    useEffect(() => {
        if (mostrarTabla) {
            obtenerSublíneas();
        }
    }, [mostrarTabla]);

    // Función para guardar una nueva sublínea o actualizar una existente
    const handleSalvar = async () => {
        try {
            // Aplicamos trim() a las variables antes de guardar
            const trimmedCveSubLin = cveSubLin.trim();
            const trimmedNomSubLin = nomSubLin.trim();
    
            if (!cveLin || !trimmedCveSubLin || !trimmedNomSubLin) {
                setMensaje('Por favor, completa todos los campos.');
                toast.current?.show({ 
                    severity: 'warn', 
                    summary: 'Campos requeridos', 
                    detail: 'Todos los campos marcados con * son obligatorios', 
                    life: 3000 
                });
                return;
            }
    
            if (mode === 'new') {
                await registrarSublinea(API_URLS.Sublineas.registrar, trimmedCveSubLin, trimmedNomSubLin, cveLin);
                toast.current?.show({ 
                    severity: 'success', 
                    summary: '¡Sublínea creada!', 
                    detail: 'La sublínea se registró exitosamente', 
                    life: 3000 
                });
            } else if (mode === 'edit') {
                const confirmacion = window.confirm('⚠️ ¿Estás seguro de que deseas actualizar esta sublínea?\n\nEsta acción modificará los datos existentes.');
                if (!confirmacion) return;
    
                await actualizarSublinea(API_URLS.Sublineas.actualizar(cveLin, trimmedCveSubLin), cveLin, trimmedCveSubLin, trimmedNomSubLin);
                toast.current?.show({ 
                    severity: 'success', 
                    summary: '¡Sublínea actualizada!', 
                    detail: 'Los cambios se guardaron correctamente', 
                    life: 3000 
                });
            }
    
            limpiarFormulario();
            setMode('view');
        } catch (error) {
            setMensaje(`Error al ${mode === 'new' ? 'registrar' : 'actualizar'} la sublínea`);
            console.error(error);
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: `No se pudo ${mode === 'new' ? 'crear' : 'actualizar'} la sublínea`, 
                life: 3000 
            });
        }
    }

    const handleEliminar = async () => {
        try {
            if (!cveLin || !cveSubLin) {
                toast.current?.show({ 
                    severity: 'warn', 
                    summary: 'Advertencia', 
                    detail: 'Seleccione una sublínea para eliminar', 
                    life: 3000 
                });
                return;
            }

            const confirmacion = window.confirm('⚠️ ¿Estás seguro de que deseas eliminar esta sublínea?\n\nEsta acción no se puede deshacer.');
            if (!confirmacion) return;

            await eliminarSublinea(API_URLS.Sublineas.eliminar(cveLin, cveSubLin));
            toast.current?.show({ 
                severity: 'success', 
                summary: '¡Sublínea eliminada!', 
                detail: 'La sublínea se eliminó correctamente', 
                life: 3000 
            });
            limpiarFormulario();
        } catch (error) {
            setMensaje('Error al eliminar la sublínea');
            console.error(error);
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error al eliminar', 
                detail: 'Esta sublínea está siendo utilizada y no se puede eliminar', 
                life: 4000 
            });
        }
    };

    // Función para habilitar el modo de edición
    const handleEditar = () => {
        if (!cveSubLin || !cveLin) {
            toast.current?.show({ 
                severity: 'warn', 
                summary: 'Advertencia', 
                detail: 'Seleccione una sublínea para editar', 
                life: 3000 
            });
            return;
        }
        setMode('edit');
    };

    // Función para crear una nueva sublínea
    const handleNuevo = () => {
        limpiarFormulario();
        setMode('new');
    };

    // Función para cancelar la operación actual
    const handleCancelar = () => {
        limpiarFormulario();
        setMode('view');
    };

    // Función para limpiar el formulario
    const limpiarFormulario = () => {
        setClave('');
        setNombre('');
        setlineaa('');
        setLinea(null);
        setSearchTerm('');
    };

    const obtenerSublíneas = async () => {
        try {
            const result = await obtenerSublineas(API_URLS.Sublineas.obtener);
            setData(result);
        } catch (error) {
            console.error('Error al obtener las sublíneas:', error);
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Error al cargar los datos', 
                life: 3000 
            });
        }
    };

    const handleConsultar = () => {
        setMostrarTabla(true);
    };

    const cerrarTabla = () => {
        setMostrarTabla(false);
        setSearchTerm('');
    };

    const handleSeleccionarSublínea = (sub: SublineaType) => {
        setClave(sub.cve);
        setlineaa(sub.cve_linea);
        setNombre(sub.nombre);
        
        // Buscar la línea correspondiente al cve_linea
        const lineaSeleccionada = arrLineas.find(l => l.cve === sub.cve_linea);
        setLinea(lineaSeleccionada || null);
        
        setMostrarTabla(false);
        setSearchTerm('');
        setMode('view');
    };

    // Función para obtener información del modo actual
    const getModeIndicator = () => {
        switch (mode) {
            case 'new':
                return { text: 'NUEVA SUBLÍNEA', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO SUBLÍNEA', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* MODAL - Tabla de Sublíneas mejorado con scroll arreglado */}
            {mostrarTabla && (
                <div className="overlay" onClick={cerrarTabla} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '800px',
                        width: '90%',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        {/* Header fijo */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                📋 Consultar Sublíneas
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={cerrarTabla}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        {/* Barra de búsqueda fija */}
                        <div style={{ marginBottom: '20px', flexShrink: 0 }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar por clave, nombre o línea..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Contador de resultados fijo */}
                        <div style={{ 
                            marginBottom: '15px', 
                            fontSize: '0.9em', 
                            color: '#6b7280',
                            padding: '8px 12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            flexShrink: 0
                        }}>
                            📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> sublíneas
                        </div>

                        {/* Contenedor de tabla con scroll */}
                        <div style={{ 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header de tabla fijo */}
                            <div style={{ 
                                backgroundColor: '#f8fafc',
                                borderBottom: '2px solid #e5e7eb',
                                flexShrink: 0
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '25%'
                                            }}>🔑 Clave</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '50%'
                                            }}>📝 Nombre</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '25%'
                                            }}>🔗 Línea</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                            
                            {/* Cuerpo de tabla con scroll */}
                            <div style={{ 
                                flex: 1,
                                overflowY: 'auto',
                                maxHeight: 'calc(80vh - 200px)'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item, index) => (
                                                <tr 
                                                    key={index} 
                                                    onClick={() => handleSeleccionarSublínea(item)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                >
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #f3f4f6',
                                                        width: '25%'
                                                    }}>{item.cve}</td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #f3f4f6',
                                                        width: '50%'
                                                    }}>{item.nombre}</td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #f3f4f6',
                                                        width: '25%'
                                                    }}>{item.cve_linea}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No hay resultados
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido principal */}
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Formulario principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '800px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Header con indicador de modo */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                🔗 Gestión de Sublíneas
                            </h2>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                backgroundColor: modeInfo.color,
                                color: 'white',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                            }}>
                                <i className={`pi ${modeInfo.icon}`}></i>
                                {modeInfo.text}
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off">
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: '20px', alignItems: 'end' }}>
                                {/* Campo Línea */}
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🔗 Línea <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={nomLin}
                                        options={arrLineas}
                                        onChange={(e) => {
                                            setLinea(e.value);
                                            setlineaa(e.value.cve);
                                        }}
                                        optionLabel="nombre"
                                        placeholder="Selecciona una línea"
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            border: `2px solid ${mode === 'view' || mode === 'edit' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px'
                                        }}
                                        disabled={mode === 'view' || mode === 'edit'}
                                    />
                                </div>

                                {/* Campo Clave */}
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🔑 Clave <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' || mode === 'edit' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' || mode === 'edit' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={cveSubLin}
                                        onChange={(e) => setClave(e.target.value)}
                                        disabled={mode === 'view' || mode === 'edit'}
                                        placeholder="Ingrese la clave"
                                    />
                                </div>

                                {/* Campo Nombre */}
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        📝 Nombre <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={nomSubLin}
                                        onChange={(e) => setNombre(e.target.value)}
                                        disabled={mode === 'view'}
                                        placeholder="Ingrese el nombre"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Botones de acción reorganizados en una sola línea */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {mode === 'view' ? (
                                <>
                                    <Button
                                        icon="pi pi-search"
                                        label="Consultar"
                                        className="p-button-info p-button-raised"
                                        onClick={handleConsultar}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-plus"
                                        label="Nuevo"
                                        className="p-button-success p-button-raised"
                                        onClick={handleNuevo}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        label="Editar"
                                        className="p-button-warning p-button-raised"
                                        onClick={handleEditar}
                                        disabled={!cveSubLin}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px',
                                            opacity: !cveSubLin ? 0.5 : 1
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        label="Eliminar"
                                        className="p-button-danger p-button-raised"
                                        onClick={handleEliminar}
                                        disabled={!cveSubLin}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px',
                                            opacity: !cveSubLin ? 0.5 : 1
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        icon="pi pi-check"
                                        label="Guardar"
                                        className="p-button-success p-button-raised"
                                        onClick={handleSalvar}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        label="Cancelar"
                                        className="p-button-secondary p-button-raised"
                                        onClick={handleCancelar}
                                        style={{ 
                                            height: '44px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            borderRadius: '8px',
                                            minWidth: '120px'
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sublineas;